import React, { useState, useEffect } from 'react'
import { 
  Clock, 
  Calendar, 
  Users, 
  FileText, 
  Database, 
  Monitor, 
  Share2, 
  MessageSquare, 
  Home,
  ExternalLink,
  Sun,
  Cloud,
  Bell,
  Settings,
  Search,
  Heart,
  LogOut
} from 'lucide-react'
import QuickAccessCard from './components/QuickAccessCard'
import WeatherWidget from './components/WeatherWidget'
import QuickNotes from './components/QuickNotes'
import Announcements from './components/Announcements'
import StaffDirectory from './components/StaffDirectory'
import AuthGate from './components/AuthGate'
import AdminPanel from './components/AdminPanel'

interface QuickLink {
  id: string
  title: string
  description: string
  url?: string
  links?: Array<{
    label: string
    url: string
  }>
  icon: React.ComponentType<any>
  category: string
  color: string
}

const quickLinks: QuickLink[] = [
  {
    id: 'pco-query',
    title: 'PCO Query App',
    description: 'Planning Center Online data queries and reports',
    url: 'https://pco-query-worker.jeremy-caf.workers.dev',
    icon: Database,
    category: 'Data & Reports',
    color: 'bg-blue-500'
  },
  {
    id: 'monday-staff-requests',
    title: 'Staff Request Forms',
    description: 'Manage staff requests and workflows',
    url: 'https://grace-staff-requests-prod.jeremy-caf.workers.dev',
    icon: Users,
    category: 'Workflow',
    color: 'bg-purple-500'
  },
  {
    id: 'congregational-care-requests',
    title: 'Congregational Care Requests',
    description: 'Submit and manage care requests for congregation members',
    links: [
      {
        label: 'Submit Request',
        url: 'https://forms.monday.com/forms/8380f71a4ca560a6f20ae3d752eabec7?r=use1'
      },
      {
        label: 'View Board',
        url: 'https://gracefm.monday.com/boards/9302420826/'
      }
    ],
    icon: Heart,
    category: 'Care & Support',
    color: 'bg-red-500'
  },
  {
    id: 'rockrms-ministry-workflows',
    title: 'RockRMS Ministry Workflows',
    description: 'Create, manage, and export professional workflows for ministry areas',
    url: 'https://ministry-workflow-builder.pages.dev',
    icon: Share2,
    category: 'Workflow',
    color: 'bg-indigo-500'
  },
  {
    id: 'pco-arrivals',
    title: 'PCO Arrivals Billboard',
    description: 'Check-in and arrivals management system',
    url: 'https://pco-arrivals-billboard-client.onrender.com/login',
    icon: Monitor,
    category: 'Check-in',
    color: 'bg-green-500'
  },
  {
    id: 'grace-budgets',
    title: 'Grace Church Budgets',
    description: 'Budget templates and planning for ministry areas',
    url: 'https://main.staff-budget-app.pages.dev',
    icon: FileText,
    category: 'Finance',
    color: 'bg-emerald-500'
  },
  {
    id: 'pco-registrations',
    title: 'PCO Registration Forms',
    description: 'Manage event registrations and sign-ups',
    url: 'https://gracefm.churchcenter.com/registrations',
    icon: Users,
    category: 'Events',
    color: 'bg-cyan-500'
  },
  {
    id: 'meeting-transcript-request',
    title: 'Meeting Transcript Request',
    description: 'Request a transcript and summary from a Teams meeting recap or recording',
    url: 'https://teams-transcription-bot.jeremy-caf.workers.dev/form',
    icon: MessageSquare,
    category: 'Communication',
    color: 'bg-teal-500'
  },
  {
    id: 'staff-promotional-requests',
    title: 'Staff Promotional Requests',
    description: 'Submit and manage promotional requests and campaigns',
    links: [
      {
        label: 'Promo Request',
        url: 'https://gracefm.churchcenter.com/people/forms/764893'
      },
      {
        label: 'Promo Board',
        url: 'https://gracefm.monday.com/boards/6605317915'
      }
    ],
    icon: MessageSquare,
    category: 'Marketing',
    color: 'bg-pink-500'
  },
  {
    id: 'sharepoint',
    title: 'Dream Team Document Library',
    description: 'SharePoint document library for team resources',
    url: 'https://gracefm.sharepoint.com/:f:/s/DreamTeamDataLibrary/EiCrtuoX7EhBpkaE8D_0omQBG_1jysP6dN6DjJJ2YzkrAg?e=whNVI0',
    icon: FileText,
    category: 'Documents',
    color: 'bg-orange-500'
  },
  {
    id: 'text-in-church',
    title: 'Text In Church',
    description: 'Text messaging and communication platform',
    url: 'https://app.textinchurch.com/login',
    icon: MessageSquare,
    category: 'Communication',
    color: 'bg-pink-500'
  },
  {
    id: 'website-admin',
    title: 'Website Admin',
    description: 'Manage and update the Grace Fellowship website',
    url: 'https://gracefm.org/wp-admin',
    icon: Settings,
    category: 'Administration',
    color: 'bg-gray-600'
  }
]

function App() {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [greeting, setGreeting] = useState('')
  const [showStaffDirectory, setShowStaffDirectory] = useState(false)
  const [showAdminPanel, setShowAdminPanel] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const hour = currentTime.getHours()
    if (hour < 12) {
      setGreeting('Good morning')
    } else if (hour < 17) {
      setGreeting('Good afternoon')
    } else {
      setGreeting('Good evening')
    }
  }, [currentTime])

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const handleLogout = () => {
    localStorage.removeItem('grace-staff-authenticated')
    setIsAuthenticated(false)
  }

  // Show authentication gate if not authenticated
  if (!isAuthenticated) {
    return <AuthGate onAuthenticated={() => setIsAuthenticated(true)} />
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img 
                src="https://thechurchco-production.s3.amazonaws.com/uploads/sites/1824/2020/02/Grace-Fellowship-Ministeries-Logo-2.png"
                alt="Grace Fellowship Ministries"
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Grace Fellowship Staff Home</h1>
                <p className="text-sm text-gray-500">Quick access to daily tools and resources</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">
                  {formatTime(currentTime)}
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(currentTime)}
                </div>
              </div>
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-gray-600" />
              </div>
              <button
                onClick={() => setShowAdminPanel(true)}
                className="p-2 text-gray-400 hover:text-grace-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Admin Panel"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            {greeting}, Staff!
          </h2>
          <p className="text-white">
            Welcome to your daily dashboard. Here's everything you need to get started.
          </p>
        </div>

        {/* Quick Access Grid */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <ExternalLink className="w-5 h-5 mr-2" />
            Quick Access
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickLinks.map((link) => (
              <QuickAccessCard key={link.id} {...link} />
            ))}
          </div>
        </div>

        {/* Additional Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Weather Widget */}
          <div className="lg:col-span-1">
            <WeatherWidget />
          </div>

          {/* Quick Notes */}
          <div className="lg:col-span-1">
            <QuickNotes />
          </div>

          {/* Announcements */}
          <div className="lg:col-span-1">
            <Announcements />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <button 
              onClick={() => setShowStaffDirectory(true)}
              className="card p-4 text-center hover:scale-105 transition-transform"
            >
              <Users className="w-8 h-8 mx-auto mb-2 text-grace-blue-600" />
              <span className="text-sm font-medium">Staff Directory</span>
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              © 2024 Grace Fellowship Church. All rights reserved.
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>Need help?</span>
              <a href="mailto:jeremy@gracefm.org" className="text-grace-blue-600 hover:text-grace-blue-700">
                Contact IT
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Staff Directory Modal */}
      {showStaffDirectory && (
        <StaffDirectory onClose={() => setShowStaffDirectory(false)} />
      )}

      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}
    </div>
  )
}

export default App 